import classNames from 'classnames';
import { useField } from 'formik';
import { forwardRef, useEffect, useRef } from 'react';

import { type CustomFieldColor } from '@air/api/types';
import { CheckIcon, Edit2Icon, Trash2Icon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Input } from '@air/primitive-input';

import { CustomField } from '~/components/CustomField';
import { CustomFieldModalColorMenu } from '~/components/CustomFields/CustomFieldModal/CustomFieldModalColorMenu';
import { type ModalCustomFieldValue } from '~/components/CustomFields/CustomFieldModal/CustomFieldModalForm/types';
import {
  CUSTOM_FIELD_DELETE_OPTION,
  CUSTOM_FIELD_MODAL_OPTION,
  CUSTOM_FIELD_MODAL_OPTION_INPUT,
  CUSTOM_FIELD_SAVE_OPTION,
} from '~/constants/testIDs';

type CustomFieldModalOptionProps = {
  colors: CustomFieldColor[];
  handleColorClick: (backgroundColor: string, index: number) => void;
  handleDeleteItem: (index: number) => void;
  handleSaveOption: () => void;
  isSaveCurrentOptionDisabled?: boolean;
  disableOptionClick?: boolean;
  index: number;
  inEditMode: boolean;
  onToggleEditMode: () => void;
  option: ModalCustomFieldValue;
};

const CustomFieldModalOption = forwardRef<HTMLDivElement, CustomFieldModalOptionProps>(
  (
    {
      colors,
      handleColorClick,
      handleDeleteItem,
      handleSaveOption,
      isSaveCurrentOptionDisabled = false,
      index,
      disableOptionClick = false,
      inEditMode,
      onToggleEditMode,
      option,
    }: CustomFieldModalOptionProps,
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const fieldName = `values.${index}.value`;

    const [field, meta] = useField(fieldName);

    // biome-ignore lint/correctness/useExhaustiveDependencies: PLA-1724 — pre-existing debt, not introduced by the icon migration
    useEffect(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, [inEditMode]);

    if (inEditMode) {
      return (
        <div data-testid="EDITING_CUSTOM_FIELD_OPTION" className="relative flex h-10 items-center gap-2">
          <Input
            ref={inputRef}
            className="w-full pl-9"
            data-1p-ignore
            size="large"
            // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
            data-testid={`${CUSTOM_FIELD_MODAL_OPTION_INPUT}-${option.value}`}
            {...field}
          />
          {!!meta.error && <span className="absolute -bottom-5 ads:text-error text-12 text-red-9">{meta.error}</span>}
          <CustomFieldModalColorMenu
            colors={colors}
            fontHex={option.color.fontHex}
            // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
            selectedColor={option.color?.backgroundHex}
            index={index}
            handleColorClick={handleColorClick}
          />
          <IconButton
            adsIcon
            onClick={handleSaveOption}
            data-testid={CUSTOM_FIELD_SAVE_OPTION}
            size="medium"
            appearance="filled"
            color="gray"
            disabled={isSaveCurrentOptionDisabled}
            icon={CheckIcon}
            label="Save"
          />
          <IconButton
            adsIcon
            data-testid={CUSTOM_FIELD_DELETE_OPTION}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onClick={() => handleDeleteItem(index)}
            size="medium"
            appearance="filled"
            color="gray"
            icon={Trash2Icon}
            label="Delete"
          />
        </div>
      );
    }

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: PLA-1724 — pre-existing debt, not introduced by the icon migration
      <div
        data-testid="CUSTOM_FIELD_MODAL_OPTION"
        data-optionid={option.id}
        className={classNames('h-10', disableOptionClick ? 'pointer-events-none' : 'pointer-events-auto')}
        onClick={onToggleEditMode}
        ref={ref}
      >
        <button
          className="group/custom-field-modal-options flex w-full cursor-pointer items-center justify-between rounded-4 p-2 ads:hover:bg-hover-subtle hover:bg-gray-4"
          // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
          data-testid={`${CUSTOM_FIELD_MODAL_OPTION}-${option.value}`}
          type="button"
        >
          <CustomField field={{ ...option, value: option.value || '' }} />
          <Edit2Icon className="hidden group-hover/custom-field-modal-options:block" size="sm" />
        </button>
      </div>
    );
  },
);

CustomFieldModalOption.displayName = 'CustomFieldModalOption';

export { CustomFieldModalOption };
